import { toSVG } from 'bwip-js/browser';
import { useMemo } from 'react';

import { errorIds, logError } from '@/lib/log-error';
import { REGULATORY } from '@/lib/regulatory';
import { cn } from '@/lib/utils';

interface UdiDataMatrixProps {
    className?: string;
}

/**
 * GS1 DataMatrix encoding the device UDI, generated locally with bwip-js
 * (no external barcode service — ESCLOUD-218). Encodes AI (01) GTIN,
 * AI (11) manufacturing date and AI (8012) software version.
 *
 * Kept on a white background in all themes: scanners require a light
 * quiet zone around the symbol.
 */
export function UdiDataMatrix({ className }: UdiDataMatrixProps) {
    // bwip-js throws on any encoding problem, during render, and the dialog
    // sits outside the viewer-scoped error boundary — an uncaught throw here
    // would blank the whole app. The dialog already shows the UDI in
    // human-readable form, so degrading to a text placeholder is acceptable.
    const svg = useMemo(() => {
        try {
            return toSVG({ bcid: 'gs1datamatrix', text: REGULATORY.gs1.elementString });
        } catch (error) {
            logError(errorIds.UDI_DATAMATRIX_RENDER_FAILED, 'Failed to encode the UDI GS1 DataMatrix', {
                elementString: REGULATORY.gs1.elementString,
                error,
            });
            return null;
        }
    }, []);

    if (svg === null) {
        return (
            <div className={cn('flex items-center bg-muted p-2 text-xs text-muted-foreground', className)}>
                UDI barcode unavailable — see human-readable UDI above.
            </div>
        );
    }

    return (
        <div
            role="img"
            aria-label={`UDI DataMatrix: ${REGULATORY.gs1.elementString}`}
            className={cn('bg-white p-2 [&_svg]:h-full [&_svg]:w-full', className)}
            dangerouslySetInnerHTML={{ __html: svg }}
        />
    );
}
